import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader, Button } from 'semantic-ui-react';
import { getDashboardChart } from '~/api/admin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { CHART_COLORS, DEPLOYMENT_TYPE_COLORS } from './chartColors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

interface ChartData {
  labels: string[];
  counts: number[];
  budgets: number[];
  datasets?: { label: string; counts: number[]; budgets: number[] }[];
}

interface DealFunnelChartProps {
  interval?: string;
  onIntervalChange?: (interval: string) => void;
  states?: string[];
}

type YAxisMode = 'count' | 'budget';

const DealFunnelChart: React.FC<DealFunnelChartProps> = ({
  interval: intervalProp = 'all',
  states = [],
}) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [yAxisMode, setYAxisMode] = useState<YAxisMode>('budget');
  const interval = intervalProp;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { interval };
        if (states.length) params.states = states.join(',');
        const result = await getDashboardChart('deal-funnel', params);
        setData(result || null);
      } catch (error) {
        console.error('Error fetching deal funnel chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [interval, states]);

  useEffect(() => {
    if (!loading && data?.labels?.length && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        const excludedLabel = 'Estimate';
        const labelIndicesToKeep =
          data.labels
            .map((label, idx) => ({ label, idx }))
            .filter(({ label }) => label !== excludedLabel)
            .map(({ idx }) => idx);

        const baseDatasets =
          data.datasets && data.datasets.length
            ? data.datasets
            : [
                {
                  label: 'All deals',
                  counts: data.counts,
                  budgets: data.budgets,
                },
              ];

        const chartDatasets = baseDatasets.map((ds) => {
          const sourceValues = yAxisMode === 'count' ? ds.counts : ds.budgets;
          const values =
            labelIndicesToKeep.length > 0
              ? labelIndicesToKeep.map((i) => sourceValues[i] ?? 0)
              : sourceValues;
          const color =
            DEPLOYMENT_TYPE_COLORS[ds.label] ?? CHART_COLORS.softBlue;
          return {
            label: ds.label,
            data: values,
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
          };
        });

        const yAxisLabel = yAxisMode === 'count' ? 'Count' : 'Budget ($)';
        const formatTooltipValue = (value: number) => {
          if (yAxisMode === 'count') {
            return `${value} deal${value !== 1 ? 's' : ''}`;
          } else {
            // Format as currency
            return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
          }
        };

        chartInstance.current = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels:
              labelIndicesToKeep.length > 0
                ? labelIndicesToKeep.map((i) => data.labels[i])
                : data.labels,
            datasets: chartDatasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: chartDatasets.length > 1,
              },
              title: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) => formatTooltipValue(context.parsed.y),
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Status',
                },
                beginAtZero: true,
                ticks: {
                  stepSize: yAxisMode === 'count' ? 1 : undefined,
                },
              },
              y: {
                title: {
                  display: true,
                  text: yAxisLabel,
                },
                beginAtZero: true,
                ticks: {
                  callback: (value) => {
                    if (yAxisMode === 'budget') {
                      // Format large numbers with K/M suffixes
                      const num = Number(value);
                      if (num >= 1000000) {
                        return `$${(num / 1000000).toFixed(1)}M`;
                      } else if (num >= 1000) {
                        return `$${(num / 1000).toFixed(0)}K`;
                      }
                      return `$${num}`;
                    }
                    return value.toString();
                  },
                },
              },
            },
          },
        });
      }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [data, loading, yAxisMode]);

  if (loading) {
    return (
      <div className="card">
        <Header as="h2">Deal Funnel</Header>
        <Loader active inline="centered" />
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Header as="h2" style={{ margin: 0 }}>Deal Funnel</Header>
        <Button.Group size="small">
          <Button
            active={yAxisMode === 'count'}
            onClick={() => setYAxisMode('count')}
          >
            Count
          </Button>
          <Button
            active={yAxisMode === 'budget'}
            onClick={() => setYAxisMode('budget')}
          >
            Budget
          </Button>
        </Button.Group>
      </div>
      {data?.labels?.length ? (
        <div style={{ height: '320px', width: '100%' }}>
          <canvas ref={chartRef} />
        </div>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

export default DealFunnelChart;
